/**
 * @fileoverview User Management Hooks for Admin Role
 * @author Hospital Room Scheduler Team
 * @created 08-10-2025 02:30:00
 * 
 * React Query hooks for user management operations in admin interface.
 * Provides data fetching, caching, and mutation capabilities for user CRUD operations.
 * 
 * Features:
 * - User listing with search and filtering
 * - User creation, updates, and role management
 * - Session management and security operations
 * - Optimistic updates and cache invalidation
 * - Error handling and loading states
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../../api/endpoints/users';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

/**
 * User search and filter parameters
 */
export interface UseUsersParams {
  /** Page number for pagination (default: 1) */
  page?: number;
  
  /** Number of items per page (default: 10) */
  limit?: number;
  
  /** Search term for name or email (optional) */
  search?: string;
  
  /** Filter by user role (optional) */
  role?: 'ADMIN' | 'COORDINATOR' | 'DOCTOR';
  
  /** Filter by active status (optional) */
  isActive?: boolean;
}

/**
 * User data structure
 */
export interface User {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'COORDINATOR' | 'DOCTOR';
  specialty?: string;
  department?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Users API response with pagination
 */
export interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * Create user data
 */
export interface CreateUserData {
  name: string;
  email: string;
  role: 'ADMIN' | 'COORDINATOR' | 'DOCTOR';
  specialty?: string;
  department?: string;
}

/**
 * Update user data
 */
export interface UpdateUserData {
  name?: string;
  email?: string;
  specialty?: string;
  department?: string;
}

// =============================================================================
// QUERY KEYS
// =============================================================================

export const userQueryKeys = {
  /** All users query key */
  all: ['users'] as const,
  
  /** Users list query key */
  list: (params: UseUsersParams) => ['users', 'list', params] as const,
  
  /** Single user query key */
  detail: (id: string) => ['users', 'detail', id] as const,
  
  /** User sessions query key */
  sessions: (id: string) => ['users', 'sessions', id] as const,
};

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Hook to fetch users with search and filtering
 * 
 * @param params - Search and filter parameters
 * @returns React Query result with users data
 * 
 * @example
 * ```tsx
 * const { data, isLoading, error } = useUsers({ 
 *   role: 'DOCTOR', 
 *   search: 'john',
 *   limit: 50 
 * });
 * ```
 */
export function useUsers(params: UseUsersParams = {}) {
  return useQuery({
    queryKey: userQueryKeys.list(params),
    queryFn: () => usersApi.getUsers(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch single user by ID
 * 
 * @param userId - User ID to fetch
 * @returns React Query result with user data
 * 
 * @example
 * ```tsx
 * const { data: user, isLoading } = useUser('user-id-123');
 * ```
 */
export function useUser(userId: string) {
  return useQuery({
    queryKey: userQueryKeys.detail(userId),
    queryFn: () => usersApi.getUser(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch user sessions
 * 
 * @param userId - User ID to fetch sessions for
 * @returns React Query result with sessions data
 * 
 * @example
 * ```tsx
 * const { data: sessions } = useUserSessions('user-id-123');
 * ```
 */
export function useUserSessions(userId: string) {
  return useQuery({
    queryKey: userQueryKeys.sessions(userId),
    queryFn: () => usersApi.getUserSessions(userId),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Hook to create a new user
 * 
 * @returns Mutation for creating users
 * 
 * @example
 * ```tsx
 * const createUserMutation = useCreateUser();
 * 
 * const handleCreate = () => {
 *   createUserMutation.mutate({
 *     name: 'Dr. John Doe',
 *     email: 'john@moh.gov.sa',
 *     role: 'DOCTOR',
 *     specialty: 'CARDIOLOGY'
 *   });
 * };
 * ```
 */
export function useCreateUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userData: CreateUserData) => usersApi.createUser(userData),
    onSuccess: () => {
      // Invalidate users list to refresh data
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
    },
  });
}

/**
 * Hook to update user information
 * 
 * @returns Mutation for updating users
 * 
 * @example
 * ```tsx
 * const updateUserMutation = useUpdateUser();
 * 
 * const handleUpdate = (userId: string) => {
 *   updateUserMutation.mutate({
 *     id: userId,
 *     userData: { name: 'Updated Name' }
 *   });
 * };
 * ```
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, userData }: { id: string; userData: UpdateUserData }) => 
      usersApi.updateUser(id, userData),
    onSuccess: (_, variables) => {
      // Invalidate specific user and users list
      queryClient.invalidateQueries({ queryKey: userQueryKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
    },
  });
}

/**
 * Hook to deactivate user account
 * 
 * @returns Mutation for deactivating users
 * 
 * @example
 * ```tsx
 * const deactivateUserMutation = useDeactivateUser();
 * 
 * const handleDeactivate = (userId: string) => {
 *   deactivateUserMutation.mutate(userId);
 * };
 * ```
 */
export function useDeactivateUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userId: string) => usersApi.deactivateUser(userId),
    onSuccess: (_, userId) => {
      // Invalidate specific user and users list
      queryClient.invalidateQueries({ queryKey: userQueryKeys.detail(userId) });
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
    },
  });
}

/**
 * Hook to activate user account
 * 
 * @returns Mutation for activating users
 * 
 * @example
 * ```tsx
 * const activateUserMutation = useActivateUser();
 * 
 * const handleActivate = (userId: string) => {
 *   activateUserMutation.mutate(userId);
 * };
 * ```
 */
export function useActivateUser() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userId: string) => usersApi.activateUser(userId),
    onSuccess: (_, userId) => {
      // Invalidate specific user and users list
      queryClient.invalidateQueries({ queryKey: userQueryKeys.detail(userId) });
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
    },
  });
}

/**
 * Hook to change user role
 * 
 * @returns Mutation for changing user roles
 * 
 * @example
 * ```tsx
 * const changeRoleMutation = useChangeUserRole();
 * 
 * const handleRoleChange = (userId: string, newRole: string) => {
 *   changeRoleMutation.mutate({ userId, role: newRole });
 * };
 * ```
 */
export function useChangeUserRole() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => 
      usersApi.changeUserRole(userId, role),
    onSuccess: (_, variables) => {
      // Invalidate specific user and users list
      queryClient.invalidateQueries({ queryKey: userQueryKeys.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
    },
  });
}

/**
 * Hook to terminate user sessions
 * 
 * @returns Mutation for terminating user sessions
 * 
 * @example
 * ```tsx
 * const terminateSessionsMutation = useTerminateUserSessions();
 * 
 * const handleTerminateSessions = (userId: string) => {
 *   terminateSessionsMutation.mutate(userId);
 * };
 * ```
 */
export function useTerminateUserSessions() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userId: string) => usersApi.terminateUserSessions(userId),
    onSuccess: (_, userId) => {
      // Invalidate user sessions
      queryClient.invalidateQueries({ queryKey: userQueryKeys.sessions(userId) });
    },
  });
}

/**
 * Hook to get user statistics
 * 
 * @returns React Query result with user statistics
 * 
 * @example
 * ```tsx
 * const { data: stats } = useUserStats();
 * ```
 */
export function useUserStats() {
  return useQuery({
    queryKey: ['users', 'stats'],
    queryFn: async () => {
      // Get all users to calculate stats
      const response = await usersApi.getUsers({ limit: 1000 });
      const users = response.users;
      
      return {
        totalUsers: users.length,
        activeUsers: users.filter(user => user.isActive).length,
        adminUsers: users.filter(user => user.role === 'ADMIN').length,
        coordinatorUsers: users.filter(user => user.role === 'COORDINATOR').length,
        doctorUsers: users.filter(user => user.role === 'DOCTOR').length,
      };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - longer cache since stats don't change often
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook to get user statistics from existing users data
 * This avoids making a separate API call by using existing data
 * 
 * @param users - Array of users to calculate stats from
 * @returns User statistics object
 * 
 * @example
 * ```tsx
 * const { data: usersData } = useUsers();
 * const stats = useUserStatsFromData(usersData?.users || []);
 * ```
 */
export function useUserStatsFromData(users: User[]) {
  return {
    totalUsers: users.length,
    activeUsers: users.filter(user => user.isActive).length,
    adminUsers: users.filter(user => user.role === 'ADMIN').length,
    coordinatorUsers: users.filter(user => user.role === 'COORDINATOR').length,
    doctorUsers: users.filter(user => user.role === 'DOCTOR').length,
  };
}